import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import classNames from "classnames";
import { DatePicker, TimePicker, Tooltip } from "@toddle-edu/ds-web";
import DateTimeFieldRow from "../DateTimeFieldRow";
import useDateTimeField from "../../hooks/useDateTimeField";
import { useDateTimeFieldsContext } from "../../context/DateTimeFieldsContext";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { formatDate, getDateMomentObject } from "Utils/DateTimeHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import classes from "./AssignmentDateTimeFields.scss";

/**
 * Reusable component for rendering a single date/time field
 * Handles both edit and read-only modes
 */
const DateTimeField = ({
  fieldName,
  label,
  tooltipText,
  dateRange,
  isEditMode,
  isDisabled,
  disabledTooltip,
  showError,
  testId,
  onValidationError,
}) => {
  const { dates } = useDateTimeFieldsContext();
  const dateTime = dates[fieldName];

  // Use custom hook to connect to context
  const { handleDateChange, handleTimeChange } = useDateTimeField(
    fieldName,
    isInvalid => {
      if (onValidationError) {
        onValidationError(isInvalid);
      }
    }
  );

  // Don't render if no date value
  if (!dateTime) {
    return null;
  }

  /**
   * Renders editable date and time pickers
   */
  const renderEditableField = () => {
    const dateTimeMoment = getDateMomentObject(dateTime);

    const datePickerContainerClasses = classNames({
      [classes.datePickerContainer]: true,
      [classes.datePickerError]: showError,
    });

    return (
      <>
        <div className={datePickerContainerClasses}>
          <ConditionalWrapper
            condition={isDisabled}
            wrapper={<Tooltip tooltip={disabledTooltip} />}
            type={"wrapperAsElement"}
          >
            <DatePicker
              onChange={handleDateChange}
              picker="date"
              format={DATE_TIME_CONSTANTS.DD_MMM_ddd}
              value={dateTimeMoment}
              variant="inline"
              inputReadOnly={true}
              minDate={_.get(dateRange, "minDate")}
              maxDate={_.get(dateRange, "maxDate")}
              testId={testId}
              dsVersion="2.0"
              disabled={isDisabled}
              hasError={showError}
              showIcon={false}
            >
              <span className={classNames("text-body", classes.dateText)}>
                {formatDate({
                  date: dateTimeMoment,
                  format: DATE_TIME_CONSTANTS.DD_MMM_ddd,
                })}
              </span>
            </DatePicker>
          </ConditionalWrapper>
        </div>
        <ConditionalWrapper
          condition={isDisabled}
          wrapper={<Tooltip tooltip={disabledTooltip} />}
          type={"wrapperAsElement"}
        >
          <div className={classes.timePickerContainer}>
            <TimePicker
              value={dateTimeMoment}
              onChange={handleTimeChange}
              disabled={isDisabled}
              variant="inline"
              dsVersion="2.0"
              hasError={showError}
              minTime={_.get(dateRange, "startTime")}
              maxTime={_.get(dateRange, "endTime")}
              minutesStep={5}
              testId={`${testId}-time-picker`}
              showIcon={false}
              useNewV3={true}
            >
              <span className={classNames("text-body", classes.timeText)}>
                {formatDate({
                  date: dateTimeMoment,
                  format: DATE_TIME_CONSTANTS.hh_mm_A,
                })}
              </span>
            </TimePicker>
          </div>
        </ConditionalWrapper>
      </>
    );
  };

  /**
   * Renders read-only date and time display
   */
  const renderReadOnlyField = () => {
    const dateTimeMoment = getDateMomentObject(dateTime);
    const datePickerContainerClasses = classNames({
      [classes.datePickerContainer]: true,
      [classes.datePickerError]: showError,
    });

    return (
      <>
        <div className={datePickerContainerClasses}>
          <div className={classNames("text-body", classes.readOnlyDateText)}>
            {formatDate({
              date: dateTimeMoment,
              format: DATE_TIME_CONSTANTS.DD_MMM_ddd,
            })}
          </div>
        </div>
        <div className={classes.verticalSeparator} />
        <div className={classNames("text-body", classes.readOnlyTimeText)}>
          {formatDate({
            date: dateTimeMoment,
            format: DATE_TIME_CONSTANTS.hh_mm_A,
          })}
        </div>
      </>
    );
  };

  return (
    <DateTimeFieldRow
      label={label}
      tooltipText={tooltipText}
      testId={testId}
      renderValue={() =>
        isEditMode ? renderEditableField() : renderReadOnlyField()
      }
    />
  );
};

DateTimeField.propTypes = {
  /** Field name in context (openDate, dueDate, closeDate, visibilityDate) */
  fieldName: PropTypes.oneOf([
    "openDate",
    "dueDate",
    "closeDate",
    "visibilityDate",
  ]).isRequired,
  /** Label text to display */
  label: PropTypes.string.isRequired,
  /** Tooltip text for info icon */
  tooltipText: PropTypes.string,
  /** Date range constraints */
  dateRange: PropTypes.object,
  /** Whether field is in edit mode */
  isEditMode: PropTypes.bool,
  /** Whether field is disabled */
  isDisabled: PropTypes.bool,
  /** Tooltip to show when disabled */
  disabledTooltip: PropTypes.node,
  /** Whether to show error styling */
  showError: PropTypes.bool,
  /** Test ID for testing */
  testId: PropTypes.string,
  /** Callback when validation error occurs */
  onValidationError: PropTypes.func,
};

DateTimeField.defaultProps = {
  tooltipText: "",
  dateRange: null,
  isEditMode: false,
  isDisabled: false,
  disabledTooltip: null,
  showError: false,
  testId: "",
  onValidationError: null,
};

export default DateTimeField;
